import React, { forwardRef, useState, useMemo, useEffect, useCallback, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../commonDesign/Button'
import Loading from '../../../molecules/Loading'
import usePipelineQuery from '../../DenaliDataPipeline/hooks/usePipelineQuery'
import FieldGroup from './FieldGroup'
import { buildDefaultValues, sortByOrdinal, coerceFormValues, getAllFields, buildArgsFromMapping } from './utils/fieldUtils'

const DynamicActionForm = forwardRef(function DynamicActionForm ({ actionTemplate, onSubmit, onCancel, subject, variables }, ref) {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)

  const {
    groups,
    dataPipelineCode,
    argMapping
  } = useMemo(() => ({
    groups: actionTemplate?.definition?.fieldMapping?.groups || [],
    dataPipelineCode: actionTemplate?.definition?.dataPipelineCode,
    argMapping: actionTemplate?.definition?.argMapping
  }), [actionTemplate])

  // optional initialization pipeline
  const initDisabled = !dataPipelineCode
  const { data: initResult, isLoading: initLoading } = usePipelineQuery(
    dataPipelineCode,
    {
      ...(variables || {}),
      subject
    },
    initDisabled
  )

  // Build default values from field definitions and init data
  const defaultValues = useMemo(() => {
    // Only build after init data loads (if pipeline exists)
    if (!initDisabled && initLoading) {
      return {
        ...(variables || {})
      } // Return empty while loading
    }

    return buildDefaultValues(groups, {
      ...(variables || {}),
      ...(initResult || {})
    })
  }, [groups, initResult, initDisabled, initLoading, variables])

  // Initialize form
  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues
  })

  // Reset form when defaultValues change (after init loads)
  useEffect(() => {
    if (Object.keys(defaultValues).length > 0) {
      form.reset(defaultValues)
    }
  }, [defaultValues, form])

  // Sort groups by ordinal
  const sortedGroups = useMemo(() => sortByOrdinal(groups), [groups])

  // Get all fields for value coercion
  const allFields = useMemo(() => getAllFields(groups), [groups])

  const handleSubmit = useCallback(async (data) => {
    setProcessing(true)
    setError(null)

    try {
      // Coerce form values to appropriate types
      const coercedData = coerceFormValues(data, allFields)

      // Build args object from argMapping
      const args = buildArgsFromMapping(coercedData, argMapping)

      // Call onSubmit with args (not raw form data)
      await onSubmit(args)

      // Reset form on success
      form.reset()
    } catch (err) {
      setError(err.message || 'An error occurred while submitting the form.')
    } finally {
      setProcessing(false)
    }
  }, [setProcessing, setError, allFields, argMapping, onSubmit, form])

  const handleCancel = useCallback((e) => {
    setError(null)
    onCancel(e)
  }, [onCancel, setError])

  useImperativeHandle(
    ref,
    () => ({
      cancel: handleCancel
    }),
    [handleCancel]
  )

  // Show loading while initialization pipeline loads (if initialization pipeline enabled)
  if (!initDisabled && initLoading) {
    return <Loading />
  }

  return (
    <>
      {sortedGroups.map((group, index) => (
        <FieldGroup
          key={group.id || `group-${index}`}
          group={group}
          control={form.control}
          form={form}
          initializationData={initResult}
        />
      ))}

      <SydModalActions>
        {error && <div className='__error'>{error}</div>}
        <SydButton
          variant='ghost'
          onClick={handleCancel}
          disabled={processing}
        >
          Cancel
        </SydButton>
        <SydButton
          variant='primary'
          onClick={form.handleSubmit(handleSubmit)}
          processing={processing}
          disabled={!form.formState.isValid}
        >
          Submit
        </SydButton>
      </SydModalActions>
    </>
  )
})

DynamicActionForm.propTypes = {
  actionTemplate: PropTypes.shape({
    definition: PropTypes.shape({
      dataPipelineCode: PropTypes.string,
      fieldMapping: PropTypes.shape({
        groups: PropTypes.arrayOf(PropTypes.object)
      }),
      argMapping: PropTypes.object
    })
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  subject: PropTypes.any,
  variables: PropTypes.object
}

export default DynamicActionForm
